import {
  IBankAccountProps,
  IGeneralTableIdentifierException,
  IPostingPeriod,
} from 'commons/types';

import { SwipeRxWmsApi } from '../resources';
import { SwipeRxAPIResources } from '../resources/types/api-resource.enum';

import {
  IInventoryAdjustmentItemRequest,
  IInventoryAdjustmentProductDetailRequest,
  IInventoryAdjustmentProductDetailResponse,
  IInventoryAdjustmentProductInfoRequest,
  IInventoryAdjustmentProductInfoResponse,
  IInventoryAdjustmentRemoveItemRequest,
  UpdateInventoryAdjustmentInfoRequest,
  IGetInventoryAdjustmentListResponseDTO,
  IInventoryAdjustmentBinRequest,
  IInventoryAdjustmentBinResponse,
} from './types';
import { IGetInventoryAdjustmentActiveLocations } from './types/get-inventory-adjustment-active-locations.type';

class SwipeRxWmsApiInventoryAdjustment extends SwipeRxWmsApi {
  private readonly basePath: string;

  constructor() {
    super(SwipeRxAPIResources.INVENTORY_ADJUSTMENT);
    this.basePath = `/${this.apiResource}`;
  }

  async getActivePostingPeriods(): Promise<IPostingPeriod[]> {
    try {
      const result = await this.get('posting-period', {
        is_active: true,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getActiveBankAccounts(): Promise<IBankAccountProps[]> {
    try {
      const result = await this.get('bank-account', {
        is_active: true,
      });
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async updateInventoryAdjustmentInfo(
    documentNumber: string,
    params: UpdateInventoryAdjustmentInfoRequest,
  ) {
    try {
      const result = await this.patch(
        `${this.basePath}/${encodeURIComponent(documentNumber)}/info`,
        params,
      );
      return result;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: true };
    }
  }

  async getProductInfo(
    params: IInventoryAdjustmentProductInfoRequest,
  ): Promise<{
    error?: string | true;
    data?: IInventoryAdjustmentProductInfoResponse;
  }> {
    try {
      const result = await this.get(`${this.basePath}/product`, params);
      const data = result.data as IInventoryAdjustmentProductInfoResponse;
      return { data };
    } catch (error: unknown) {
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: true };
    }
  }

  async getProductDetail(
    params: IInventoryAdjustmentProductDetailRequest,
  ): Promise<{
    error?: string | true;
    data?: IInventoryAdjustmentProductDetailResponse;
  }> {
    try {
      const result = await this.get(`${this.basePath}/product-detail`, params);
      const data = result.data as IInventoryAdjustmentProductDetailResponse;
      return { data };
    } catch (error: unknown) {
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: true };
    }
  }

  async getBin(params: IInventoryAdjustmentBinRequest): Promise<{
    error?: string | true;
    data?: IInventoryAdjustmentBinResponse;
  }> {
    try {
      const result = await this.get(`${this.basePath}/bin`, params);
      const data = result.data as IInventoryAdjustmentBinResponse;
      return { data };
    } catch (error: unknown) {
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: true };
    }
  }

  async postInventoryAdjustmentItem(params: IInventoryAdjustmentItemRequest) {
    try {
      const result = await this.patch(`${this.basePath}/item`, params);
      return result;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return { error: error.message };
      }
      return { error: true };
    }
  }

  async postRemoveInventoryAdjustmentItem(
    params: IInventoryAdjustmentRemoveItemRequest,
  ): Promise<void> {
    try {
      await this.patch(`${this.basePath}/remove-item`, params);
    } catch (error) {
      // TODO send alert ?
    }
  }

  async postCompleteInventoryAdjustment(
    documentNumber: string,
  ): Promise<IGeneralTableIdentifierException[] | null> {
    try {
      await this.post(
        `${this.basePath}/${encodeURIComponent(documentNumber)}/complete`,
        {},
      );
      return null;
    } catch (error: unknown) {
      if (Array.isArray(error) && error.length > 0) {
        return error as IGeneralTableIdentifierException[];
      }
      throw error;
    }
  }

  async removeInventoryAdjustment(documentNumber: string): Promise<void> {
    try {
      await this.post(`${this.basePath}/${documentNumber}/cancel`, {});
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getInventoryAdjustments(): Promise<IGetInventoryAdjustmentListResponseDTO> {
    try {
      const result = await this.get(`${this.basePath}`);
      return result;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async postNewInventoryAdjustment() {
    try {
      const result = await this.post(`${this.basePath}`, {});
      return result;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }

  async getActiveLocations(): Promise<
    IGetInventoryAdjustmentActiveLocations[]
  > {
    try {
      const result = await this.get(`${this.basePath}/active-locations`);
      return result.data;
    } catch (error) {
      console.warn(error);
      throw error;
    }
  }
}

export const swiperxWmsApiInventoryAdjustment =
  new SwipeRxWmsApiInventoryAdjustment();
